import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import IconArrowUp from '@/assets/icons/icon-arrow-up-01.svg?react';
import React, { useState, useRef } from 'react';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, PROPERTY } from '@/analytics/mixpanel';
import { useSelector } from 'react-redux';
import { useEditor, Element } from '@craftjs/core';
import { ROOT_NODE } from '@craftjs/utils';
import { xmlToCraftJson } from '@builder4/shared/xml-dsl';
import { CraftJsonDto, type VersionedSectionNodes } from '@builder4/shared/dto/craft/CraftJsonDto';
import { runCraftJsonMigrations } from '@builder4/shared/migrations/craft';
import { runStyleJsonMigrations } from '@builder4/shared/migrations/style';
import { validateStylesConfig } from '@builder4/shared/dto/styles/validateStylesConfig';
import { validate } from 'class-validator';
import { formatValidationErrors } from '@builder4/shared/utils/validators';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import {
  orchestratePageProfileGeneration,
  extractStyleFromHtml,
  streamHtmlToDsl,
  type PageProfileInput,
  type PageProfileProgress,
  type GeneratedSection,
  type ModelOption,
  type AiGenerationStats,
  hasAnthropicApiKey,
  hasOpenAiApiKey,
} from '@/services/ai-orchestrator';
import { StreamingXmlSectionParser } from '@/utils/streamingXmlParser';
import AiGenerationLoader from '@/components/userComponents/AiGenerationLoader';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { createGlobalStyle, applyGlobalStyle } from '@/store/globalStyles/thunks';
import { useAppDispatch } from '@/store/hooks';
import type { PageProps } from '@builder4/shared/components/Page';
import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import { showToast } from '@/store/notifications/slice';

type GenerationMode = 'html' | 'page-profile';

interface AiBuilderProps {
  className?: string;
  onClose?: () => void;
  /** Optional structured page profile input for Page Profile mode */
  pageProfileInput?: PageProfileInput;
}

export const AiBuilder: React.FC<AiBuilderProps> = ({ className, onClose, pageProfileInput }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionCount, setSectionCount] = useState(0);
  const [generationPhase, setGenerationPhase] = useState<'idle' | 'planning' | 'style' | 'page'>(
    'idle',
  );
  // Default to page-profile mode if pageProfileInput is provided
  const [generationMode, setGenerationMode] = useState<GenerationMode>(
    pageProfileInput ? 'page-profile' : 'html',
  );
  const [hqProgressMessage, setHqProgressMessage] = useState('');
  const showAnthropic = hasAnthropicApiKey();
  const showOpenAi = hasOpenAiApiKey();

  const getDefaultModel = (): ModelOption => {
    if (showAnthropic) return 'sonnet-4.6';
    if (showOpenAi) return 'gpt-4.1';
    return 'sonnet-4.6';
  };

  const [selectedModel, setSelectedModel] = useState<ModelOption>(getDefaultModel);

  const track = useMixpanel();
  const generationStartRef = useRef<number>(0);
  const makeStats = (): AiGenerationStats => ({
    totalInputTokens: 0,
    totalOutputTokens: 0,
    planDurationMs: 0,
    sectionDurations: [],
    sectionInputTokens: 0,
    sectionOutputTokens: 0,
    trackStage: (action, data) => {
      track(EVENT.AI_STAGE_COMPLETED, {
        [PROPERTY.AI_ACTION]: action,
        [PROPERTY.AI_MODEL]: data.model,
        ...(data.inputTokens !== undefined && { [PROPERTY.AI_INPUT_TOKENS]: data.inputTokens }),
        ...(data.outputTokens !== undefined && { [PROPERTY.AI_OUTPUT_TOKENS]: data.outputTokens }),
        ...(data.inputTokens !== undefined &&
          data.outputTokens !== undefined && {
            [PROPERTY.AI_TOTAL_TOKENS]: data.inputTokens + data.outputTokens,
          }),
        ...(data.durationMs !== undefined && { [PROPERTY.AI_DURATION_MS]: data.durationMs }),
        ...(data.sectionCount !== undefined && { [PROPERTY.AI_SECTION_COUNT]: data.sectionCount }),
        ...(data.longestSectionDurationMs !== undefined && {
          [PROPERTY.AI_LONGEST_SECTION_DURATION_MS]: data.longestSectionDurationMs,
        }),
      });
    },
  });
  const statsRef = useRef<AiGenerationStats>(makeStats());

  // State for screenshot upload (HTML mode)
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);

  // State for pasted page profile JSON
  const [pastedProfileJson, setPastedProfileJson] = useState('');
  const [parsedProfile, setParsedProfile] = useState<PageProfileInput | null>(null);
  const [profileParseError, setProfileParseError] = useState<string | null>(null);

  const globalStyles = useSelector(selectActiveGlobalStyle);
  const pageId = useSelector(selectCurrentPageId);
  const dispatch = useAppDispatch();
  const { actions, query } = useEditor();
  const { getSectionNodeTreeWithUniqueIds } = useCraftHelper();

  const loaderNodeIdRef = useRef<string | null>(null);
  const parserRef = useRef<StreamingXmlSectionParser | null>(null);
  const fullXmlRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const sectionNodeIdsRef = useRef<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const addLoaderToPage = () => {
    const newNode = query.parseReactElement(<Element is={AiGenerationLoader} />).toNodeTree();
    actions.addNodeTree(newNode, ROOT_NODE);
    loaderNodeIdRef.current = newNode.rootNodeId;
  };

  const insertSectionBeforeLoader = async (sectionXml: string) => {
    // Convert section XML to CraftJS JSON
    const result = xmlToCraftJson(sectionXml);

    if (!result.success || !result.sectionJson) {
      console.error('Section conversion failed:', result.errors);
      return;
    }

    // Migrate and validate the section JSON
    let migratedSectionJson: VersionedSectionNodes;
    try {
      migratedSectionJson = await migrateAndValidateSection(result.sectionJson);
    } catch (error) {
      console.error('Section migration/validation failed:', error);
      return;
    }

    // Convert to NodeTree with unique IDs
    const nodeTree = getSectionNodeTreeWithUniqueIds(migratedSectionJson);

    if (!loaderNodeIdRef.current) {
      // Loader not found, just add to end
      actions.addNodeTree(nodeTree, ROOT_NODE);
      return;
    }

    // Get loader's current index in ROOT children
    const rootNode = query.node(ROOT_NODE).get();
    const loaderIndex = rootNode.data.nodes.indexOf(loaderNodeIdRef.current);

    if (loaderIndex === -1) {
      // Loader not found in ROOT, just add to end
      actions.addNodeTree(nodeTree, ROOT_NODE);
    } else {
      // Insert section at loader's index (pushes loader down)
      actions.addNodeTree(nodeTree, ROOT_NODE, loaderIndex);
    }

    sectionNodeIdsRef.current.push(nodeTree.rootNodeId);
    setSectionCount((prev) => prev + 1);
  };

  const removeLoaderFromPage = () => {
    if (loaderNodeIdRef.current) {
      try {
        actions.delete(loaderNodeIdRef.current);
      } catch (e) {
        console.error('Failed to remove loader:', e);
      }
      loaderNodeIdRef.current = null;
    }
  };

  const migrateAndValidateSection = async (
    sectionJson: VersionedSectionNodes,
  ): Promise<VersionedSectionNodes> => {
    const dto = CraftJsonDto.fromSection(sectionJson);

    const migrationResult = runCraftJsonMigrations(dto);
    if (migrationResult.error) {
      throw new Error(`Migration failed: ${migrationResult.errorMessage}`);
    }

    const validationErrors = await validate(migrationResult.json);
    if (validationErrors.length > 0) {
      const formattedErrors = formatValidationErrors(validationErrors);
      throw new Error(`Validation failed: ${formattedErrors.join(', ')}`);
    }

    return migrationResult.json.toSectionFormat();
  };

  const handleSectionComplete = (sectionXml: string) => {
    console.log(`[AiBuilder] Section ${sectionNodeIdsRef.current.length + 1} XML:`, sectionXml);
    insertSectionBeforeLoader(sectionXml);
  };

  const handlePageTag = (pageTagXml: string) => {
    console.log('[AiBuilder] Page tag XML:', pageTagXml);
    // Parse the self-closing page tag using the converter
    const result = xmlToCraftJson(pageTagXml);

    if (!result.success || !result.pageJson) {
      console.error('Page tag conversion failed:', result.errors);
      return;
    }

    // Extract props from the ROOT node
    const pageProps = result.pageJson.ROOT?.props;
    if (!pageProps) {
      console.error('No page props found in conversion result');
      return;
    }

    // Apply parsed props to the existing ROOT page node
    actions.setProp(ROOT_NODE, (props: Partial<PageProps>) => {
      Object.assign(props, pageProps);
    });
  };

  const fireGenerationEvent = (success: boolean, errorMessage?: string) => {
    const s = statsRef.current;
    const sectionCount = sectionNodeIdsRef.current.length;
    const longestSection =
      s.sectionDurations.length > 0 ? Math.max(...s.sectionDurations) : undefined;

    track(EVENT.AI_GENERATION, {
      [PROPERTY.AI_GENERATION_MODE]: generationMode,
      [PROPERTY.AI_MODEL]: selectedModel,
      [PROPERTY.AI_SUCCESS]: success,
      [PROPERTY.AI_TOTAL_DURATION_MS]: Date.now() - generationStartRef.current,
      [PROPERTY.AI_INPUT_TOKENS]: s.totalInputTokens,
      [PROPERTY.AI_OUTPUT_TOKENS]: s.totalOutputTokens,
      [PROPERTY.AI_TOTAL_TOKENS]: s.totalInputTokens + s.totalOutputTokens,
      [PROPERTY.AI_SECTION_COUNT]: sectionCount,
      ...(s.planDurationMs > 0 && { [PROPERTY.AI_PLAN_DURATION_MS]: s.planDurationMs }),
      ...(longestSection !== undefined && {
        [PROPERTY.AI_LONGEST_SECTION_DURATION_MS]: longestSection,
      }),
      ...(generationMode === 'html' && { [PROPERTY.AI_HAS_SCREENSHOT]: !!screenshotBase64 }),
      ...(errorMessage !== undefined && { [PROPERTY.AI_ERROR]: errorMessage }),
    });
  };

  const handleGenerationComplete = () => {
    fireGenerationEvent(true);
    removeLoaderFromPage();
    setIsGenerating(false);
    setGenerationPhase('idle');
    setHqProgressMessage('');
    parserRef.current = null;
    abortControllerRef.current = null;
  };

  const handleGenerationError = (err: Error) => {
    console.error('Generation error:', err);
    fireGenerationEvent(false, err.message);
    setError(err.message);
    removeLoaderFromPage();
    setIsGenerating(false);
    setGenerationPhase('idle');
    setHqProgressMessage('');
    parserRef.current = null;
    abortControllerRef.current = null;
  };

  const applyGeneratedStyle = async (style: StylesConfigDto, label: string): Promise<void> => {
    if (!pageId) return;

    const migrationResult = runStyleJsonMigrations(style);
    if (migrationResult.error) {
      throw new Error(`Style migration failed: ${migrationResult.errorMessage}`);
    }

    const validationResult = validateStylesConfig(migrationResult.json);
    if (!validationResult.isValid) {
      throw new Error(`Style validation failed: ${validationResult.errorMessages.join(', ')}`);
    }

    const newStyle = await dispatch(
      createGlobalStyle({
        label,
        global_style: migrationResult.json,
        showSuccessToast: true,
      }),
    ).unwrap();

    await dispatch(
      applyGlobalStyle({
        pageId,
        globalStyleId: newStyle.id,
        showSuccessToast: false,
      }),
    ).unwrap();
  };

  const handleImageUpload = (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setScreenshotPreview(dataUrl);
      // Strip the data:image/...;base64, prefix
      const base64 = dataUrl.split(',')[1];
      setScreenshotBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveScreenshot = () => {
    setScreenshotBase64(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ── HTML mode generation ──
  const handleGenerateHtml = async () => {
    if (!prompt.trim() || isGenerating) return;

    console.log('[HTML] HTML-to-DSL generation started');
    generationStartRef.current = Date.now();
    statsRef.current = makeStats();
    setIsGenerating(true);
    setError(null);
    setSectionCount(0);
    sectionNodeIdsRef.current = [];

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Stage 1: Extract style from HTML
      setGenerationPhase('style');
      const { style, label } = await extractStyleFromHtml(
        prompt,
        abortController.signal,
        screenshotBase64 ?? undefined,
        selectedModel,
        statsRef.current,
      );

      try {
        await applyGeneratedStyle(style, label);
        console.log('[HTML] Style extracted and applied');
      } catch (err) {
        console.error('Failed to apply extracted style:', err);
        dispatch(
          showToast({
            text: 'Failed to apply extracted style. Proceeding with existing style.',
            state: 'error',
          }),
        );
      }

      // Stage 2: Stream HTML-to-DSL conversion
      setGenerationPhase('page');
      addLoaderToPage();
      fullXmlRef.current = '';

      parserRef.current = new StreamingXmlSectionParser({
        onSectionComplete: handleSectionComplete,
        onPageTag: handlePageTag,
        onPageStart: () => console.log('[HTML] DSL conversion started'),
        onPageComplete: () => {
          console.log('[HTML] DSL conversion complete');
          console.log('[HTML] Full XML:', fullXmlRef.current);
        },
      });

      await streamHtmlToDsl(
        prompt,
        style,
        {
          onTextDelta: (text) => {
            fullXmlRef.current += text;
            parserRef.current?.append(text);
          },
          onComplete: () => {
            parserRef.current?.finalize();
            handleGenerationComplete();
          },
          onError: handleGenerationError,
        },
        abortController.signal,
        screenshotBase64 ?? undefined,
        selectedModel,
      );
    } catch (err) {
      handleGenerationError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  // ── Page Profile mode generation ──
  const handleGeneratePageProfile = async () => {
    if (!activePageProfileInput || isGenerating) return;

    console.log('[PageProfile] Page generation started');
    generationStartRef.current = Date.now();
    statsRef.current = makeStats();
    setIsGenerating(true);
    setError(null);
    setSectionCount(0);
    sectionNodeIdsRef.current = [];
    setGenerationPhase('planning');

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      await orchestratePageProfileGeneration(
        {
          input: activePageProfileInput,
          existingStyles: globalStyles,
          signal: abortController.signal,
          model: selectedModel,
          stats: statsRef.current,
        },
        {
          onProgress: (progress: PageProfileProgress) => {
            setHqProgressMessage(progress.message);
            if (progress.phase === 'styling') {
              setGenerationPhase('style');
            } else if (progress.phase === 'generating-sections') {
              setGenerationPhase('page');
            }
          },
          onPlanComplete: (outline) => {
            console.log('[PageProfile] Outline complete:', outline.sections.length, 'sections');
          },
          onStyleGenerated: async (style, label) => {
            try {
              await applyGeneratedStyle(style, label);
            } catch (err) {
              console.error('Failed to apply page profile style:', err);
              dispatch(
                showToast({
                  text: 'Failed to apply generated style.',
                  state: 'error',
                }),
              );
            }
          },
          onSectionComplete: (section: GeneratedSection) => {
            console.log(`[PageProfile] Section ${section.index + 1} complete:`, section.summary);
            handleSectionComplete(section.xml);
          },
          onComplete: () => {
            console.log('[PageProfile] Page generation complete');
            console.log(`[PageProfile] Total sections: ${sectionNodeIdsRef.current.length}`);
            handleGenerationComplete();
          },
          onError: handleGenerationError,
        },
      );
    } catch (err) {
      handleGenerationError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const handleGenerate = () => {
    if (generationMode === 'page-profile') {
      handleGeneratePageProfile();
    } else {
      handleGenerateHtml();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleClear = () => {
    if (isGenerating) return;

    // Get all children of ROOT
    const rootNode = query.node(ROOT_NODE).get();
    const childIds = rootNode.data.nodes;

    // Delete all existing children
    childIds.forEach((childId: string) => {
      try {
        actions.delete(childId);
      } catch (e) {
        console.error('Failed to delete node:', e);
      }
    });
  };

  const getStatusMessage = (): string => {
    if (generationMode === 'page-profile' && hqProgressMessage) {
      return hqProgressMessage;
    }
    if (generationPhase === 'style') {
      return 'Extracting design system...';
    }
    if (generationPhase === 'page') {
      return `Converting HTML to page... ${sectionCount > 0 ? `(${sectionCount} sections)` : ''}`;
    }
    if (generationPhase === 'planning') {
      return 'Planning page structure...';
    }
    return `Generating... ${sectionCount > 0 ? `(${sectionCount} sections)` : ''}`;
  };

  // Get the active page profile input (prop takes precedence over pasted)
  const activePageProfileInput = pageProfileInput || parsedProfile;

  // Check if the generate button should be enabled
  const hasApiKey = showAnthropic || showOpenAi;
  const canGenerate =
    hasApiKey &&
    (generationMode === 'page-profile' ? !!activePageProfileInput : prompt.trim().length > 0);

  // Handle pasted JSON changes
  const handleProfileJsonChange = (value: string) => {
    setPastedProfileJson(value);
    setProfileParseError(null);
    setParsedProfile(null);

    if (!value.trim()) {
      return;
    }

    try {
      const parsed = JSON.parse(value);
      // Basic validation - check for required fields (title, description, images)
      if (!parsed.title || typeof parsed.title !== 'string') {
        setProfileParseError('Missing title field');
        return;
      }
      if (!parsed.description || typeof parsed.description !== 'string') {
        setProfileParseError('Missing description field');
        return;
      }
      if (!Array.isArray(parsed.images)) {
        setProfileParseError('Missing images array');
        return;
      }
      setParsedProfile(parsed as PageProfileInput);
    } catch {
      setProfileParseError('Invalid JSON format');
    }
  };

  return (
    <div className={clsx(className, 'flex h-full w-[280px] flex-col bg-white dark:bg-neutral-900')}>
      <PanelTopHeader
        text="AI Page Generator"
        backButton={false}
        onClickClose={handleClose}
        className={''}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Instructions */}
        <div className="flex flex-col gap-2 px-6 pt-4">
          <p className="text-xs leading-relaxed text-neutral-600 dark:text-neutral-400">
            Paste your HTML to convert it into an editable page, or use a Page Profile to generate
            from structured data.
          </p>
        </div>

        {/* Status Display */}
        {isGenerating && (
          <div className="mx-6 mt-4 rounded-lg bg-indigo-50 px-4 py-3 dark:bg-indigo-900/20">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-500 [animation-delay:-0.3s]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-500 [animation-delay:-0.15s]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-500" />
              </div>
              <span className="text-sm text-indigo-700 dark:text-indigo-300">
                {getStatusMessage()}
              </span>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 rounded-lg bg-red-50 px-4 py-3 dark:bg-red-900/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Input Section - Fixed to bottom */}
        <div className="flex flex-col gap-3 border-t border-neutral-200 px-6 py-4 dark:border-neutral-700">
          {/* Mode Toggle */}
          <div className="flex items-center gap-1 rounded-lg bg-neutral-100 p-0.5 dark:bg-neutral-800">
            <button
              type="button"
              disabled={isGenerating}
              className={clsx(
                'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                generationMode === 'html'
                  ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-700 dark:text-neutral-100'
                  : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200',
                isGenerating && 'cursor-not-allowed opacity-50',
              )}
              onClick={() => setGenerationMode('html')}
            >
              HTML
            </button>
            <button
              type="button"
              disabled={isGenerating}
              className={clsx(
                'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                generationMode === 'page-profile'
                  ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-700 dark:text-neutral-100'
                  : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200',
                isGenerating && 'cursor-not-allowed opacity-50',
              )}
              onClick={() => setGenerationMode('page-profile')}
            >
              Profile
            </button>
          </div>

          {/* Model Selector */}
          {showAnthropic || showOpenAi ? (
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-500 dark:text-neutral-400">Model</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value as ModelOption)}
                disabled={isGenerating}
                className={clsx(
                  'flex-1 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-xs text-neutral-700 focus:border-indigo-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
                  isGenerating && 'cursor-not-allowed opacity-50',
                )}
              >
                {showAnthropic && (
                  <optgroup label="Anthropic">
                    <option value="haiku-4.5">Haiku 4.5</option>
                    <option value="sonnet-4.5">Sonnet 4.5</option>
                    <option value="sonnet-4.6">Sonnet 4.6</option>
                    <option value="opus-4.5">Opus 4.5</option>
                    <option value="opus-4.6">Opus 4.6</option>
                  </optgroup>
                )}
                {showOpenAi && (
                  <optgroup label="OpenAI">
                    <option value="gpt-4.1">GPT-4.1</option>
                    <option value="gpt-5.2">GPT-5.2</option>
                  </optgroup>
                )}
              </select>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                No API keys configured. Set{' '}
                <code className="font-mono">VITE_ANTHROPIC_API_KEY</code> or{' '}
                <code className="font-mono">VITE_OPENAI_API_KEY</code> in your environment.
              </p>
            </div>
          )}

          {/* Page Profile mode info - show profile summary if provided via prop */}
          {generationMode === 'page-profile' && !isGenerating && pageProfileInput && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-800 dark:bg-indigo-900/20">
              <p className="mb-2 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                Page Profile Mode
              </p>
              <p className="text-xs text-indigo-600 dark:text-indigo-400">
                <strong>{pageProfileInput.title}</strong>
              </p>
              <p className="mt-1 text-xs text-indigo-500 dark:text-indigo-500">
                {pageProfileInput.images.length} images •{' '}
                {pageProfileInput.testimonials?.reviews?.length || 0} reviews
              </p>
            </div>
          )}

          {/* Page Profile mode - JSON paste textarea when no prop provided */}
          {generationMode === 'page-profile' && !isGenerating && !pageProfileInput && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                Paste your page profile JSON below:
              </p>
              <textarea
                value={pastedProfileJson}
                onChange={(e) => handleProfileJsonChange(e.target.value)}
                placeholder='{"title": "...", "description": "...", "images": [...], ...}'
                disabled={isGenerating}
                className={clsx(
                  'h-32 w-full resize-none rounded-lg border bg-neutral-50 px-3 py-2.5 font-mono text-xs leading-relaxed text-neutral-700 placeholder:text-neutral-400 focus:outline-none dark:bg-neutral-800 dark:text-neutral-200 dark:placeholder:text-neutral-500',
                  profileParseError
                    ? 'border-red-300 focus:border-red-500 dark:border-red-700'
                    : parsedProfile
                      ? 'border-green-300 focus:border-green-500 dark:border-green-700'
                      : 'border-neutral-200 focus:border-indigo-500 dark:border-neutral-700',
                  isGenerating && 'cursor-not-allowed opacity-50',
                )}
              />
              {profileParseError && (
                <p className="text-xs text-red-600 dark:text-red-400">{profileParseError}</p>
              )}
              {parsedProfile && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-2 dark:border-green-800 dark:bg-green-900/20">
                  <p className="text-xs text-green-700 dark:text-green-300">
                    <strong>{parsedProfile.title}</strong>
                  </p>
                  <p className="mt-0.5 text-xs text-green-600 dark:text-green-400">
                    {parsedProfile.images.length} images •{' '}
                    {parsedProfile.testimonials?.reviews?.length || 0} reviews
                  </p>
                </div>
              )}
            </div>
          )}

          {/* HTML Input - shown in HTML mode */}
          {generationMode === 'html' && (
            <div className="flex flex-col gap-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Paste your HTML here..."
                disabled={isGenerating}
                className={clsx(
                  'h-32 w-full resize-none rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 font-mono text-xs leading-relaxed text-neutral-700 placeholder:text-neutral-400 focus:border-indigo-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:placeholder:text-neutral-500',
                  isGenerating && 'cursor-not-allowed opacity-50',
                )}
              />

              {/* Screenshot upload */}
              {!screenshotPreview ? (
                <div
                  className={clsx(
                    'flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-neutral-300 px-3 py-2 transition-colors hover:border-indigo-400 hover:bg-indigo-50/50 dark:border-neutral-600 dark:hover:border-indigo-500 dark:hover:bg-indigo-900/10',
                    isGenerating && 'pointer-events-none opacity-50',
                  )}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) handleImageUpload(file);
                  }}
                  onPaste={(e) => {
                    const items = e.clipboardData.items;
                    for (const item of items) {
                      if (item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (file) handleImageUpload(file);
                        break;
                      }
                    }
                  }}
                  tabIndex={0}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file);
                    }}
                  />
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    Add screenshot for better accuracy (optional)
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-2 dark:border-green-800 dark:bg-green-900/20">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="h-10 w-10 rounded object-cover"
                  />
                  <span className="flex-1 truncate text-xs text-green-700 dark:text-green-300">
                    Screenshot attached
                  </span>
                  <button
                    type="button"
                    disabled={isGenerating}
                    className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    onClick={handleRemoveScreenshot}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={isGenerating}
              className={clsx(
                'flex h-10 items-center gap-2 rounded-lg border px-4 transition-colors',
                !isGenerating
                  ? 'border-neutral-300 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-800'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-300 dark:border-neutral-700 dark:text-neutral-600',
              )}
              onClick={handleClear}
            >
              <span className="text-sm font-medium">Clear</span>
            </button>
            <button
              type="button"
              disabled={!canGenerate || isGenerating}
              className={clsx(
                'flex h-10 items-center gap-2 rounded-lg px-4 transition-colors',
                canGenerate && !isGenerating
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'cursor-not-allowed bg-neutral-100 text-neutral-300 dark:bg-neutral-800 dark:text-neutral-600',
              )}
              onClick={handleGenerate}
            >
              <span className="text-sm font-medium">Generate</span>
              <IconArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
